// Wave 20.49bz — Real 3D → frontal-plane area projection.
//
// The previous frontal area came from a 2D segmentation pixel
// count. On any non-perfect-frontal camera, the rider's torso
// pitch changed the silhouette in the wrong direction (leaning
// forward INFLATED the apparent area from a side/3-quarter
// camera, so CdA rose when it should have fallen).
//
// This module reconstructs a coarse 3D "capsule" body from
// MediaPipe pose world landmarks (returned in meters relative
// to the rider's hip midpoint, real depth included), then
// projects each capsule onto the plane perpendicular to the
// rider's forward-motion axis. The projected polygon's area is
// the real frontal area a wind stream sees.
//
// Not SMPL. Not per-vertex. It's an eight-capsule articulated
// body (head, torso, two upper arms, two forearms, two thighs
// combined with shins) with per-rider width from bodyDimsM.
// Accuracy is a big step up from the 2D pixel count — the
// direction of pitch is guaranteed correct because we project
// onto a well-defined plane instead of an arbitrary camera
// plane, and the rider's stored body dims scale the widths.
//
// Higher-fidelity replacements (real SMPL + LBS or an HMR-class
// regressor) can drop in behind the same signature.

import type { WorldLandmark } from './poseDetector';

const LM = {
  NOSE: 0,
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26,
  LEFT_ANKLE: 27,
  RIGHT_ANKLE: 28,
};

export interface BodyDimsM {
  shoulder_width?: number | null;
  hip_width?: number | null;
  torso_length?: number | null;
  upper_arm?: number | null;
}

interface Vec3 { x: number; y: number; z: number }

function sub(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}
function add(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}
function scale(a: Vec3, k: number): Vec3 {
  return { x: a.x * k, y: a.y * k, z: a.z * k };
}
function len(a: Vec3): number {
  return Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
}
function normalize(a: Vec3): Vec3 {
  const L = len(a);
  return L > 1e-6 ? scale(a, 1 / L) : { x: 0, y: 0, z: 0 };
}
function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}
function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

/**
 * Project point `p` onto the 2D plane defined by orthonormal
 * basis vectors `u` (horizontal) and `v` (vertical). Returns
 * pixel-space coords in metres.
 */
function project(p: Vec3, u: Vec3, v: Vec3): [number, number] {
  return [dot(p, u), dot(p, v)];
}

/**
 * Rectangular projection of a capsule (segment from A to B with
 * radius r) onto a plane. The rectangle spans from A to B along
 * the segment axis, extended by ±r perpendicular. Returned as
 * four ordered 2D corners.
 */
function capsuleRect(
  a3: Vec3,
  b3: Vec3,
  radiusM: number,
  u: Vec3,
  v: Vec3,
): Array<[number, number]> {
  const [ax, ay] = project(a3, u, v);
  const [bx, by] = project(b3, u, v);
  const dx = bx - ax;
  const dy = by - ay;
  const L = Math.hypot(dx, dy);
  if (L < 1e-6) {
    // Point-like from this angle — approximate as a circle
    // (square bounding box).
    return [
      [ax - radiusM, ay - radiusM],
      [ax + radiusM, ay - radiusM],
      [ax + radiusM, ay + radiusM],
      [ax - radiusM, ay + radiusM],
    ];
  }
  const nx = -dy / L;
  const ny = dx / L;
  return [
    [ax + nx * radiusM, ay + ny * radiusM],
    [ax - nx * radiusM, ay - ny * radiusM],
    [bx - nx * radiusM, by - ny * radiusM],
    [bx + nx * radiusM, by + ny * radiusM],
  ];
}

function polygonArea(poly: Array<[number, number]>): number {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const [x0, y0] = poly[i];
    const [x1, y1] = poly[(i + 1) % poly.length];
    a += x0 * y1 - x1 * y0;
  }
  return Math.abs(a) / 2;
}

/**
 * Union of overlapping rectangle polygons via a raster grid.
 * Good enough for capsule silhouettes at ~5 mm pixel resolution
 * on a ~1m² frame.
 */
function rasterUnionArea(
  polys: Array<Array<[number, number]>>,
  gridStepM = 0.01,
): number {
  if (polys.length === 0) return 0;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of polys) {
    for (const [x, y] of p) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  const w = Math.max(1, Math.ceil((maxX - minX) / gridStepM));
  const h = Math.max(1, Math.ceil((maxY - minY) / gridStepM));
  // Cap the grid so a wild landmark can't blow up the sampler.
  const maxCells = 200_000;
  if (w * h > maxCells) {
    // Fall back to sum-of-areas (over-counts overlaps but bounded).
    let s = 0;
    for (const p of polys) s += polygonArea(p);
    return s;
  }
  const grid = new Uint8Array(w * h);
  for (const poly of polys) {
    // Poly bounds in grid coords.
    let pMinX = Infinity, pMinY = Infinity, pMaxX = -Infinity, pMaxY = -Infinity;
    for (const [x, y] of poly) {
      if (x < pMinX) pMinX = x; if (y < pMinY) pMinY = y;
      if (x > pMaxX) pMaxX = x; if (y > pMaxY) pMaxY = y;
    }
    const gxMin = Math.max(0, Math.floor((pMinX - minX) / gridStepM));
    const gyMin = Math.max(0, Math.floor((pMinY - minY) / gridStepM));
    const gxMax = Math.min(w - 1, Math.ceil((pMaxX - minX) / gridStepM));
    const gyMax = Math.min(h - 1, Math.ceil((pMaxY - minY) / gridStepM));
    for (let gy = gyMin; gy <= gyMax; gy++) {
      for (let gx = gxMin; gx <= gxMax; gx++) {
        const cx = minX + (gx + 0.5) * gridStepM;
        const cy = minY + (gy + 0.5) * gridStepM;
        if (pointInPolygon(cx, cy, poly)) grid[gy * w + gx] = 1;
      }
    }
  }
  let filled = 0;
  for (let i = 0; i < grid.length; i++) if (grid[i]) filled++;
  return filled * gridStepM * gridStepM;
}

function pointInPolygon(x: number, y: number, poly: Array<[number, number]>): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1];
    const xj = poly[j][0], yj = poly[j][1];
    const intersect =
      (yi > y) !== (yj > y) &&
      x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

const DEFAULT_DIMS: Required<BodyDimsM> = {
  shoulder_width: 0.45,
  hip_width: 0.36,
  torso_length: 0.55,
  upper_arm: 0.30,
};

function resolveDims(d: BodyDimsM | null | undefined): Required<BodyDimsM> {
  return {
    shoulder_width: d?.shoulder_width ?? DEFAULT_DIMS.shoulder_width,
    hip_width: d?.hip_width ?? DEFAULT_DIMS.hip_width,
    torso_length: d?.torso_length ?? DEFAULT_DIMS.torso_length,
    upper_arm: d?.upper_arm ?? DEFAULT_DIMS.upper_arm,
  };
}

/**
 * Compute real frontal area (m²) from MediaPipe world landmarks.
 *
 * `forwardAxis` is the rider-motion axis in world-landmark space.
 * MediaPipe world landmarks are camera-anchored: +X right,
 * +Y down, +Z out of the screen (toward the camera). If the
 * bike faces the camera, rider-forward ≈ +Z, so we project
 * onto the (X, -Y) plane. If the camera is 3/4 to the rider,
 * the caller passes a heading-corrected axis.
 *
 * Returns null when landmarks aren't usable.
 */
export function frontalAreaFromWorldLandmarks(
  world: WorldLandmark[] | null,
  bodyDims: BodyDimsM | null | undefined,
  forwardAxis: Vec3 = { x: 0, y: 0, z: 1 },
): number | null {
  if (!world || world.length < 29) return null;
  const dims = resolveDims(bodyDims);

  // Confidence gate — require the core landmarks to be visible.
  const need = [
    LM.LEFT_SHOULDER, LM.RIGHT_SHOULDER,
    LM.LEFT_HIP, LM.RIGHT_HIP,
    LM.LEFT_ELBOW, LM.RIGHT_ELBOW,
    LM.LEFT_KNEE, LM.RIGHT_KNEE,
  ];
  for (const idx of need) {
    const lm = world[idx];
    if (!lm || (lm.visibility ?? 1) < 0.35) return null;
  }

  // Build the frontal-plane basis. Forward = rider-motion axis;
  // "up" = world up (–Y in MediaPipe world space). We project
  // onto the plane perpendicular to forward, spanned by the
  // horizontal component of the world-right axis and the vertical.
  const fwd = normalize(forwardAxis);
  const worldUp: Vec3 = { x: 0, y: -1, z: 0 };
  let right = cross(worldUp, fwd);
  if (len(right) < 1e-4) {
    // Forward parallel to world up — fall back to +X.
    right = { x: 1, y: 0, z: 0 };
  }
  const u = normalize(right); // horizontal plane axis
  const v = normalize(cross(fwd, u)); // vertical plane axis

  const p = (i: number): Vec3 => ({ x: world[i].x, y: world[i].y, z: world[i].z });

  const shoulderMid = scale(add(p(LM.LEFT_SHOULDER), p(LM.RIGHT_SHOULDER)), 0.5);
  const hipMid = scale(add(p(LM.LEFT_HIP), p(LM.RIGHT_HIP)), 0.5);

  // Torso: capsule from shoulder-mid to hip-mid, width = mean of
  // shoulder & hip widths.
  const torsoWidth = 0.5 * (dims.shoulder_width + dims.hip_width);
  const torso = capsuleRect(shoulderMid, hipMid, torsoWidth / 2, u, v);

  // Head: capsule from shoulder-mid up by ~torso_length/3, radius
  // ~10 cm. Nose landmark is above shoulders; use it as head top.
  const headTop = world[LM.NOSE] ? p(LM.NOSE) : add(shoulderMid, { x: 0, y: -0.2, z: 0 });
  const head = capsuleRect(shoulderMid, headTop, 0.09, u, v);

  // Upper arms: capsule from shoulder to elbow, radius ~5 cm.
  const armR = 0.05;
  const lUpArm = capsuleRect(p(LM.LEFT_SHOULDER), p(LM.LEFT_ELBOW), armR, u, v);
  const rUpArm = capsuleRect(p(LM.RIGHT_SHOULDER), p(LM.RIGHT_ELBOW), armR, u, v);
  // Forearms: elbow → wrist.
  const lForearm = world[LM.LEFT_WRIST]
    ? capsuleRect(p(LM.LEFT_ELBOW), p(LM.LEFT_WRIST), armR * 0.85, u, v)
    : null;
  const rForearm = world[LM.RIGHT_WRIST]
    ? capsuleRect(p(LM.RIGHT_ELBOW), p(LM.RIGHT_WRIST), armR * 0.85, u, v)
    : null;

  // Thighs: hip → knee, radius ~7 cm.
  const legR = 0.07;
  const lThigh = capsuleRect(p(LM.LEFT_HIP), p(LM.LEFT_KNEE), legR, u, v);
  const rThigh = capsuleRect(p(LM.RIGHT_HIP), p(LM.RIGHT_KNEE), legR, u, v);
  // Shins: knee → ankle, radius ~5 cm.
  const lShin = world[LM.LEFT_ANKLE]
    ? capsuleRect(p(LM.LEFT_KNEE), p(LM.LEFT_ANKLE), 0.05, u, v)
    : null;
  const rShin = world[LM.RIGHT_ANKLE]
    ? capsuleRect(p(LM.RIGHT_KNEE), p(LM.RIGHT_ANKLE), 0.05, u, v)
    : null;

  const polys: Array<Array<[number, number]>> = [
    torso, head, lUpArm, rUpArm, lThigh, rThigh,
  ];
  if (lForearm) polys.push(lForearm);
  if (rForearm) polys.push(rForearm);
  if (lShin) polys.push(lShin);
  if (rShin) polys.push(rShin);

  const areaM2 = rasterUnionArea(polys, 0.01);
  // Sanity clamp: realistic rider frontal areas are 0.25 - 0.65 m².
  // Reject wild outliers (bad landmark frame) by returning null so
  // the caller can fall back to the 2D area.
  if (!Number.isFinite(areaM2) || areaM2 < 0.1 || areaM2 > 1.2) return null;
  return areaM2;
}
